import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Plus, Crosshair, Pencil, Trash2, UserPlus } from "lucide-react";

export default function DepartmentsPanel({ departments, agents, live, onCreate, onUpdate, onDelete, onHire, onFocus, onClose }) {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");

  const create = async () => {
    if (!name.trim()) return;
    await onCreate({ name: name.trim(), purpose: purpose.trim() });
    setName(""); setPurpose("");
  };

  return (
    <div className="max-w-5xl mx-auto pt-8 px-4 pb-24">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg">Departments</h2>
        <button data-testid="departments-close-button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
      </div>

      <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] p-4 mb-6">
        <div className="text-cyan-300 text-xs font-tactical font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5"><Plus size={14} /> Build New Room</div>
        <div className="grid sm:grid-cols-[1fr_2fr_auto] gap-3 items-end">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Name</Label>
            <Input data-testid="new-dept-name-input" className="bg-[#0b0e14] border-white/10 text-zinc-100" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Legal" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Purpose</Label>
            <Input className="bg-[#0b0e14] border-white/10 text-zinc-100" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="What happens in this room?" />
          </div>
          <Button data-testid="create-dept-button" onClick={create} className="bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs h-10">Construct</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {departments.map((d) => {
          const deptAgents = agents.filter((a) => a.department_id === d.id);
          const activeCount = deptAgents.filter((a) => { const l = live[a.id]; return l && (l.phase === "working" || l.phase === "walking"); }).length;
          const color = d.color || "#00f0ff";
          return (
            <div key={d.id} data-testid="department-card" className="rounded-xl border p-4 relative overflow-hidden" style={{ borderColor: `${color}33`, background: `linear-gradient(160deg, ${color}0d, rgba(8,11,16,0.9))` }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${color}1f`, color, border: `1px solid ${color}44` }}>{d.code}</span>
                <h3 className="font-tactical font-bold uppercase tracking-wide text-zinc-100 truncate">{d.name}</h3>
              </div>
              <p className="text-[11px] text-zinc-500 leading-snug min-h-[28px] mb-3">{d.purpose || "No purpose set."}</p>
              <div className="flex items-center gap-3 text-[11px] font-mono text-zinc-400 mb-3">
                <span>{deptAgents.length} agents</span>
                <span className="text-emerald-400">{activeCount} active</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => onFocus(d)} className="h-8 px-2 text-cyan-300 hover:bg-cyan-500/10 text-xs"><Crosshair size={13} className="mr-1" /> Focus</Button>
                <Button size="sm" variant="ghost" onClick={() => onHire(d)} className="h-8 px-2 text-zinc-300 hover:bg-white/10 text-xs"><UserPlus size={13} className="mr-1" /> Hire</Button>
                <Button size="sm" variant="ghost" onClick={() => { const n = window.prompt("Rename room:", d.name); if (n && n.trim()) onUpdate(d.id, { name: n.trim() }); }} className="h-8 px-2 text-zinc-400 hover:bg-white/10"><Pencil size={13} /></Button>
                <Button size="sm" variant="ghost" onClick={() => { if (window.confirm(`Demolish ${d.name} and its agents?`)) onDelete(d.id); }} className="h-8 px-2 text-red-400 hover:bg-red-500/10 ml-auto"><Trash2 size={13} /></Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
